<template>
  <v-card tile elevation="0" class="overflow-auto">
    <v-skeleton-loader v-if="$apollo.loading" type="date-picker, card" />
    <v-row v-else-if="clientInformation" class="px-2 mx-1">
      <v-col cols="12" class="pt-0" justify="center" align="center">
        <client-action-bar :userId="userId" />
      </v-col>
      <v-col>
        <div class="d-flex justify-space-between">
          <div class="pl-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-avatar v-bind="attrs" v-on="on" size="70" color="error" class="align-self-center">
                  <img v-if="clientInformation.profilePicture" :src="clientInformation.profilePicture" />
                  <span v-else class="white--text headline">{{
                    `${clientInformation.user.firstName[0]} ${clientInformation.user.lastName[0]}`
                  }}</span>
                </v-avatar>
              </template>
              <span class="block">
                Outstanding todo's
              </span>
              <span v-for="todo in outstandingUserTodos" :key="todo.id" class="block">
                {{ todo.title }}
              </span>
              <span v-if="outstandingUserTodos.length == 0">
                No outstanding todos
              </span>
            </v-tooltip>
          </div>
          <div class="pl-2 d-flex flex-column flex-fill">
            <h3>
              <v-tooltip v-if="clientInformation.taskPauseUntil" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-alarm-snooze</v-icon>
                </template>
                <span class="block">Tasks snoozed until: {{ clientInformation.taskPauseUntil }}</span>
                <span class="block">Reason: {{ clientInformation.taskPauseNote }}</span>
              </v-tooltip>
              <v-icon v-if="clientInformation.atRiskOnboarding" color="red">mdi-exclamation-thick</v-icon>
              {{ `${clientInformation.user.firstName} ${clientInformation.user.lastName}` }}
            </h3>
            <h3>{{ clientInformation.phone }}</h3>
            <h3>{{ clientInformation.user.email }}</h3>
          </div>
          <div class="text-right">
            <h3 class="block">{{ points }} Points</h3>
            <corporate-challenge-icon v-if="corporateChallengeTitle" :title="corporateChallengeTitle" />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon large :color="subscription.color" v-bind="attrs" v-on="on" slot="activator" class="mt-2">
                  {{ subscription.icon }}
                </v-icon>
              </template>
              <span> {{ product | humanize }}</span>
            </v-tooltip>
          </div>
        </div>
        <coach
          v-if="clientInformation.activeSubscription"
          :coach="clientInformation.activeSubscription.coach"
          :subscription-id="parseInt(clientInformation.activeSubscription.id)"
          @coach-updated="refetchClient()"
          class="mt-4"
        />
        <birthday-chip v-if="clientInformation.dateOfBirth" :date-of-birth="clientInformation.dateOfBirth.toString()" />
        <hr />
        <span class="block">Date Joined: {{ fancyDate(clientInformation.user.dateJoined) }}</span>
        <span class="block" :class="[lastActive.color, lastActive.weight]"
          >Last Active on web app: {{ fancyDate(clientInformation.lastActive) }}</span
        >
        <span v-if="subscriptionEnd" class="block red--text font-weight-medium"
          >Subscription end date: {{ fancyDate(subscriptionEnd) }}
          <v-icon>
            mdi-circle-question
          </v-icon>
        </span>
        <span v-if="clientInformation.lastOneOnOneCoach" class="block"
          >Last coach: {{ clientInformation.lastOneOnOneCoach.firstName }}
          {{ clientInformation.lastOneOnOneCoach.lastName }}</span
        >
        <div class="d-flex justify-space-between">
          <span> Location: {{ clientInformation.suburb }} {{ clientInformation.country.code }}</span>
          <span> {{ clientInformation.occupation }}</span>
        </div>

        <div>
          <span v-if="clientInformation.dateOfBirth">
            Age: {{ $moment().diff(clientInformation.dateOfBirth, "years", false) }}</span
          >
          <br />
          <span v-if="clientInformation.gymBranch">
            Gym: {{ clientInformation.gymBranch.gym.title }} - {{ clientInformation.gymBranch.title }}<br
          /></span>

          <template v-if="clientInformation.activeSubscription">
            <template v-if="clientInformation.activeSubscription.corporateChallenge">
              <br />
              <b>Corporate: </b> {{ clientInformation.activeSubscription.corporateChallenge.title }}
            </template>
            <template v-if="clientInformation.activeSubscription.complementaryCategory">
              <br />
              <b>Complementary: </b> {{ clientInformation.activeSubscription.complementaryCategory.title }}
            </template>
          </template>
        </div>
        <hr />
        <div v-if="clientInformation.lastPayment">
          <b>Last Payment:</b>
          {{
            clientInformation.lastPayment.order &&
            clientInformation.lastPayment.order.subscriptionProductVariant != null
              ? clientInformation.lastPayment.order.subscriptionProductVariant.product.code
              : ""
          }}

          <br />
          <b>Discount(s): </b>{{ getDiscounts(clientInformation.lastPayment) }}
          <br />
          <b>Total:</b> {{ clientInformation.lastPayment.totalAmount }}
          <br />
          <b>Transaction date:</b>
          {{ lastTransactionDate }}
          <br />
          <span v-if="clientInformation.lastPayment.order"
            ><b>Next Billing Date:</b> {{ clientInformation.lastPayment.order.nextBillingDate }}</span
          >
        </div>
        <hr />
        <div>
          <b>Last Check-in: </b>
          <template v-if="clientInformation.lastCompletedCheckIn">
            {{ clientInformation.lastCompletedCheckIn.assessmentDate | moment("from") }}
            <div>
              <span v-if="clientInformation.lastCompletedCheckIn.rating"
                >Exercise rating: {{ clientInformation.lastCompletedCheckIn.rating }}<br
              /></span>
              <span v-if="clientInformation.lastCompletedCheckIn.comments"
                >Comments: {{ clientInformation.lastCompletedCheckIn.comments }}<br
              /></span>
              <span v-if="clientInformation.lastCompletedCheckIn.nutritionRating"
                >Nutrition rating: {{ clientInformation.lastCompletedCheckIn.nutritionRating }}<br
              /></span>
              <span v-if="clientInformation.lastCompletedCheckIn.nutritionComments"
                >Comments: {{ clientInformation.lastCompletedCheckIn.nutritionComments }}<br
              /></span>
            </div>
          </template>
          <template v-else> No check-in </template>
        </div>
        <div class="mt-4">
          <b>Weight: </b>
          <span>
            {{ clientInformation.weight }}
          </span>
          <template v-if="clientInformation.desiredWeight">
            <b>Goal weight</b> {{ clientInformation.desiredWeight }}
          </template>
        </div>
        <div>
          <b>Goals: </b>
          <span v-for="userTag in clientInformation.user.usertagSet" :key="userTag.id"> {{ userTag.tag.title }}, </span>
        </div>
      </v-col>
      <v-col cols="12" align="center" justify="center">
        <client-activity-summary
          :userId="userId"
          :current-exercise-block="clientInformation.currentBlocks"
          :current-nutrition-block="clientInformation.nutritionCurrentBlock"
        />
      </v-col>
      <v-col cols="12" align="center" justify="center">
        <client-week-dots :userId="userId" show-calendar show-arrows />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { CLIENT_INFORMATION_DATA_QUERY, OUTSTANDING_TODOS_QUERY } from "@/graphql/queries/user";
import { USER_TOTAL_POINTS_QUERY } from "@/graphql/queries/user";
import ClientActionBar from "@/components/client/blocks/ClientActionBar";
import BirthdayChip from "@/components/dashboard/components/BirthdayChip";
import ClientWeekDots from "@/components/client/blocks/progress/ClientWeekDots";
import { fancy_date } from "@/lib/fancy_date";
import ClientActivitySummary from "@/components/client/blocks/activity/ClientActivitySummary";
import Coach from "@/components/client/blocks/Coach";
import { extractNodes } from "@/lib/array";
import CorporateChallengeIcon from "@/components/shared/user/CorporateChallengeIcon";

export default {
  components: {
    ClientActionBar,
    BirthdayChip,
    ClientWeekDots,
    ClientActivitySummary,
    Coach,
    CorporateChallengeIcon,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      clientInformation: null,
      points: 0,
      updateCoach: false,
      staff: [],
    };
  },
  filters: {
    humanize(val) {
      return val?.replaceAll("_", " ");
    },
  },
  computed: {
    lastTransactionDate() {
      const transactionDate = this.clientInformation.lastPayment.transactionSet.filter(
        (t) => t.transactionStatus === "SUCCESS"
      );
      if (transactionDate && transactionDate[0] && transactionDate[0].createdAt) {
        return this.$moment(transactionDate[0].createdAt).format("YYYY-MM-DD");
      }
      return null;
    },
    corporateChallengeTitle() {
      return this.clientInformation?.activeSubscription?.corporateChallenge?.title;
    },
    lastActive() {
      let active = this.$moment(this.clientInformation.lastActive);
      let since = this.$moment().diff(active, "days");

      let value = {
        color: "red--text",
        weight: "font-weight-bold",
      };

      if (since <= 2) {
        value.color = "green--text";
        value.weight = "";
      } else if (since <= 6) {
        value.color = "orange--text";
        value.weight = "font-weight-medium";
      }

      return value;
    },
    product() {
      return this.clientInformation?.activeSubscription?.product?.type ?? "None";
    },
    subscription() {
      let value = {
        color: "",
        icon: "",
      };

      switch (this.product) {
        case "None":
          value.icon = "mdi-account-cancel";
          value.color = "grey darken-2";
          break;
        case "JEFF_MEMBER":
          value.color = "jeff-life";
          value.icon = "mdi-account-lock";
          break;
        case "CLUB":
          value.color = "club";
          value.icon = "mdi-cards-club";
          break;
        case "ONE_ON_ONE":
          value.color = "one-on-one";
          value.icon = "mdi-numeric-1-box-multiple-outline";
          break;
        case "GRAD":
          value.color = "one-on-one";
          value.icon = "mdi-account-school";
          break;
      }

      return value;
    },
    subscriptionEnd() {
      return this.clientInformation?.activeSubscription?.endDate;
    },
  },
  methods: {
    fancyDate(date) {
      return fancy_date(date);
    },
    refetchClient() {
      this.$apollo.queries.clientInformation?.refetch(this.userId);
    },
    getDiscounts(payment) {
      const discounts = extractNodes(payment?.discounts?.edges);
      if (discounts?.length > 0) {
        const codes = discounts.map((val) => {
          return val.code;
        });
        return codes.join(", ");
      }
      return null;
    },
  },
  apollo: {
    clientInformation: {
      query: CLIENT_INFORMATION_DATA_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
    },
    outstandingUserTodos: {
      query: OUTSTANDING_TODOS_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
    },
    rewardPoints: {
      query: USER_TOTAL_POINTS_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.points = response.data?.rewardPoints?.totalPoints ?? 0;
        }
      },
    },
  },
};
</script>
